import React, { useState, useRef, useEffect } from 'react';
import { captureElement, extractHtmlContent } from './html2ImageUtils';
import './Html2Image.css';

const Html2Image = () => {
  const [htmlContent, setHtmlContent] = useState(
    '<div style="padding: 20px; background-color: #f0f0f0; text-align: center;">\n' +
    '  <h1 style="color: #1976d2;">Hello, World!</h1>\n' +
    '  <p style="color: #424242;">This is a sample HTML content to capture.</p>\n' +
    '  <button style="background-color: #1976d2; color: white; border: none; padding: 10px 20px; border-radius: 4px; cursor: pointer;">Click Me</button>\n' +
    '</div>'
  );
  const [processedHtml, setProcessedHtml] = useState('');
  const [capturedImage, setCapturedImage] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [imageFormat, setImageFormat] = useState('png');
  const [imageQuality, setImageQuality] = useState(0.9);
  const [error, setError] = useState(null);
  const [isFullHtml, setIsFullHtml] = useState(false);
  
  const previewRef = useRef(null);
  const iframeRef = useRef(null);
  const canvasRef = useRef(null);
  const previewWrapperRef = useRef(null);
  const iframeWrapperRef = useRef(null);
  
  // Process HTML content when it changes
  useEffect(() => {
    try {
      // Check if this is a full HTML document
      const isFullHtmlDoc = htmlContent.includes('<!DOCTYPE') || 
                          htmlContent.includes('<html') || 
                          htmlContent.includes('<head');
      
      setIsFullHtml(isFullHtmlDoc);
      
      if (!isFullHtmlDoc) {
        const processed = extractHtmlContent(htmlContent);
        setProcessedHtml(processed);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error processing HTML:', err);
      setError('Error processing HTML. Please check your HTML syntax.');
    }
  }, [htmlContent]);

  // Set up iframe content
  useEffect(() => {
    if (isFullHtml && iframeRef.current) {
      const iframe = iframeRef.current;
      iframe.srcdoc = htmlContent;
    }
  }, [isFullHtml, htmlContent]);

  // Set up container sizing and prevent overflow issues
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (previewRef.current && previewWrapperRef.current) {
        // Ensure preview content doesn't force container to resize
        previewWrapperRef.current.style.overflow = 'auto';
      }
    });
    
    if (previewRef.current) {
      resizeObserver.observe(previewRef.current);
    }
    
    return () => {
      if (previewRef.current) {
        resizeObserver.disconnect();
      }
    };
  }, [processedHtml]);

  const handleHtmlChange = (e) => {
    setHtmlContent(e.target.value);
  };

  const handleFormatChange = (e) => {
    setImageFormat(e.target.value);
  };

  const handleQualityChange = (e) => {
    setImageQuality(parseFloat(e.target.value));
  };

  const handleCapture = async () => {
    if ((isFullHtml && !iframeRef.current) || (!isFullHtml && !previewRef.current)) return;
    
    setIsCapturing(true);
    setError(null);
    setCapturedImage(null);
    
    try {
      let result;
      
      if (isFullHtml) {
        // For full HTML, we'll use html2canvas directly on the iframe wrapper
        if (iframeWrapperRef.current) {
          // Wait a bit to ensure iframe content is fully rendered
          await new Promise(resolve => setTimeout(resolve, 500));
          
          result = await captureElement(
            iframeWrapperRef.current,
            {
              format: imageFormat,
              quality: imageQuality,
              backgroundColor: '#ffffff',
              captureIframe: true
            },
            canvasRef.current
          );
        } else {
          throw new Error('Preview container not found');
        }
      } else {
        // Capture regular preview
        result = await captureElement(
          previewRef.current, 
          {
            format: imageFormat,
            quality: imageQuality,
            backgroundColor: '#ffffff'
          },
          canvasRef.current
        );
      }
      
      setCapturedImage(result);
    } catch (error) {
      console.error('Error capturing element:', error);
      setError('Failed to capture the HTML content. Try simplifying your HTML or removing external resources.');
    } finally {
      setIsCapturing(false);
    }
  };

  const handleDownload = () => {
    if (!capturedImage) return;
    
    const link = document.createElement('a');
    link.href = capturedImage;
    link.download = `html-capture.${imageFormat}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="html2image tool-container">
      <div className="container">
        <div className="tool-header">
          <h1>HTML to Image</h1>
          <p>Convert HTML elements to images instantly</p>
        </div>
        
        <div className="tool-content">
          <div className="html2image-workspace">
            <div className="html-editor-container">
              <h3>HTML Editor</h3>
              <textarea 
                className="html-editor" 
                value={htmlContent} 
                onChange={handleHtmlChange}
                placeholder="Enter your HTML content here..."
                spellCheck="false"
              />
            </div>
            
            <div className="html-preview-container">
              <h3>Preview {isFullHtml ? '(Full HTML Document)' : ''}</h3>
              <div className="preview-wrapper" ref={previewWrapperRef}>
                {isFullHtml ? (
                  <div ref={iframeWrapperRef} className="iframe-container">
                    <iframe 
                      ref={iframeRef}
                      className="html-preview-iframe"
                      title="HTML Preview"
                      sandbox="allow-same-origin allow-scripts"
                    />
                  </div>
                ) : (
                  <div 
                    className="html-preview" 
                    ref={previewRef} 
                    dangerouslySetInnerHTML={{ __html: processedHtml }} 
                  />
                )}
              </div>
              
              <div className="capture-controls">
                <div className="control-group">
                  <label htmlFor="format-select">Image Format:</label>
                  <select 
                    id="format-select" 
                    value={imageFormat} 
                    onChange={handleFormatChange}
                    className="format-select"
                  >
                    <option value="png">PNG</option>
                    <option value="jpeg">JPEG</option>
                    <option value="webp">WebP</option>
                  </select>
                </div>
                
                {(imageFormat === 'jpeg' || imageFormat === 'webp') && (
                  <div className="control-group">
                    <label htmlFor="quality">Quality: {Math.round(imageQuality * 100)}%</label>
                    <input 
                      type="range" 
                      id="quality" 
                      min="0.1" 
                      max="1" 
                      step="0.1" 
                      value={imageQuality} 
                      onChange={handleQualityChange} 
                    />
                  </div>
                )}
                
                {error && (
                  <div className="error-message">
                    {error}
                  </div>
                )}
                
                <button 
                  className="btn btn-primary capture-btn" 
                  onClick={handleCapture}
                  disabled={isCapturing}
                >
                  {isCapturing ? 'Capturing...' : 'Capture as Image'}
                </button>
              </div>
            </div>
          </div>
          
          {capturedImage && (
            <div className="captured-result">
              <h3>Captured Image</h3>
              <div className="captured-image-container">
                <img src={capturedImage} alt="Captured HTML" />
              </div>
              <button className="btn btn-primary download-btn" onClick={handleDownload}>
                Download Image
              </button>
            </div>
          )}
          
          {/* Hidden canvas for image processing */}
          <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
        </div>
      </div>
    </div>
  );
};

export default Html2Image; 